import { useEffect, useState } from "react";
import { useLocation } from "react-router";

import { fullscreenchange, webkitfullscreenchange } from "common/constants/eventTypes";
import { useSelector } from "common/utilities/react-redux";
import { upperCase } from "common/utilities/upperCase";
import { useDeepMemo, useDeepUpdateEffect } from "common/utilities/useDeep";
import { useTranslate } from "common/utilities/useTranslate";
import { useUpdateEffect } from "common/utilities/useUpdateEffect";
import { getIdToken, getIsAuthenticated } from "domains/authentication/selectors";
import { getListedCollections } from "domains/collection/selectors";
import { getSiteLanguage } from "domains/language/selectors";
import { getActiveModals } from "domains/modal/selectors";
import {
  buildCreate,
  buildDiscover,
  buildHome,
  buildModerate,
  buildMonitor,
  buildMore,
} from "domains/nav/helpers/buildMenuItems";
import { executeHostCommand } from "domains/nav/helpers/executeHostCommand";
import { extractSubnavTerms } from "domains/nav/helpers/extractSubnavTerms";
import { getActiveItemId } from "domains/nav/helpers/getActiveItemId";
import { supportifyLanguage } from "domains/nav/helpers/supportifyLanguage";
import { useGlobalNavInit } from "domains/nav/hooks/useGlobalNavInit";
import { useRemoteEventHandler } from "domains/nav/hooks/useRemoteEventHandler";
import { executeNotificationsRemoteEvent, NotificationsRemoteEvent } from "domains/nav/notifications/notifications";
import { HostCommand } from "domains/nav/types/hostCommand";
import { getNotificationBadgeCount } from "domains/notifications/selectors";
import { useTheme } from "domains/theme/useTheme";
import { showWhatsNewIcon } from "domains/whatsNew/selectors";
import { getCanDisplayBookmarks, getCanEditSources, getCanViewSources } from "entities/permissions/selectors";

export const GlobalNav = () => {
  const [initialised, setInitialised] = useState<boolean>(false);

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-expect-error
  // eslint-disable-next-line import/no-unresolved
  useEffect(() => void import("globalnav/global-nav-app"), []);
  useGlobalNavInit(() => setInitialised(true));

  return (
    <>
      <global-nav />
      {initialised && <Effects />}
    </>
  );
};

const Effects = () => {
  const { pathname } = useLocation();
  const customTopicCount = useSelector((state) => state.topic.customTopicsCount);
  const activeModals = useSelector(getActiveModals);
  const canDisplayBookmarks = useSelector(getCanDisplayBookmarks);
  const canViewSources = useSelector(getCanViewSources);
  const canEditSources = useSelector(getCanEditSources);
  const idToken = useSelector(getIdToken);
  const isAuthenticated = useSelector(getIsAuthenticated);
  const listed = useSelector(getListedCollections);
  const notificationCount = useSelector(getNotificationBadgeCount);
  const siteLanguage = useSelector(getSiteLanguage);
  const showWhatsNew = useSelector(showWhatsNewIcon);
  const theme = useTheme();
  const translate = useTranslate();

  const language = supportifyLanguage(siteLanguage);
  const listedCollectionDetails = useDeepMemo(() => listed.map(({ id, name }) => ({ id, name })), [listed]);
  const notificationsModalIsOpen = activeModals.some((modal) => modal.id === "Notifications");
  const subnavTerms = extractSubnavTerms(translate);
  const upperCaseTheme = upperCase(theme);

  useRemoteEventHandler();

  useUpdateEffect(() => executeHostCommand(HostCommand.AUTH_SET_TOKEN, { value: idToken ?? null }), [idToken]);
  useUpdateEffect(() => executeHostCommand(HostCommand.SET_LANGUAGE, { value: language }), [language]);
  useUpdateEffect(() => executeHostCommand(HostCommand.SET_THEME, { all: upperCaseTheme }), [upperCaseTheme]);

  useDeepUpdateEffect(() => {
    executeHostCommand(HostCommand.SUBNAV_ITEM_DATA_UPDATE, buildHome(subnavTerms.home));
  }, [subnavTerms.home]);

  useDeepUpdateEffect(() => {
    executeHostCommand(
      HostCommand.SUBNAV_ITEM_DATA_UPDATE,
      buildDiscover(subnavTerms.discover, listedCollectionDetails, customTopicCount),
    );
  }, [customTopicCount, listedCollectionDetails, subnavTerms.discover]);

  useDeepUpdateEffect(() => {
    executeHostCommand(HostCommand.SUBNAV_ITEM_DATA_UPDATE, buildMonitor(subnavTerms.monitor));
  }, [subnavTerms.monitor]);

  useDeepUpdateEffect(() => {
    executeHostCommand(HostCommand.SUBNAV_ITEM_DATA_UPDATE, buildCreate(subnavTerms.create));
  }, [subnavTerms.create]);

  useDeepUpdateEffect(() => {
    const updateMore = () =>
      executeHostCommand(
        HostCommand.SUBNAV_ITEM_DATA_UPDATE,
        buildMore(subnavTerms.more, isAuthenticated, showWhatsNew, theme, canDisplayBookmarks),
      );

    updateMore();

    document.addEventListener(webkitfullscreenchange, updateMore);
    document.addEventListener(fullscreenchange, updateMore);

    return () => {
      document.removeEventListener(webkitfullscreenchange, updateMore);
      document.removeEventListener(fullscreenchange, updateMore);
    };
  }, [canDisplayBookmarks, isAuthenticated, showWhatsNew, subnavTerms.more, theme]);

  useDeepUpdateEffect(() => {
    executeHostCommand(HostCommand.SUBNAV_ITEM_DATA_UPDATE, buildModerate(subnavTerms.moderate));
  }, [canViewSources, canEditSources, subnavTerms.moderate]);

  useUpdateEffect(() => {
    const value = getActiveItemId(pathname);
    if (value === undefined) executeHostCommand(HostCommand.SUBNAV_ITEM_ACTIVE_CLEAR, undefined);
    else executeHostCommand(HostCommand.SUBNAV_ITEM_ACTIVE_SET, { value });
  }, [pathname]);

  useUpdateEffect(() => {
    if (!notificationsModalIsOpen)
      executeNotificationsRemoteEvent(NotificationsRemoteEvent.NOTIFICATIONS_CLOSED, undefined);
  }, [notificationsModalIsOpen]);

  useEffect(() => {
    executeNotificationsRemoteEvent(NotificationsRemoteEvent.NOTIFICATIONS_COUNT_UPDATE, { value: notificationCount });
  }, [notificationCount]);

  return null;
};
